import React, { Component } from "react";
import LoadingBar from "Shared/LoadingBar";
import { analyticsFEA } from "src/AnalyticsFEA";
import CreditRatingDisclaimer from "src/components/CreditRatingDisclaimer";
import CreditRatingForm, { FormState as CreditRatingFormState } from "src/components/CreditRatingForm/CreditRatingForm";
import CreditRatingInitial from "src/components/CreditRatingInitial";
import CreditRatingResultNoMatch from "src/components/CreditRatingResultNoMatch";
import CreditRatingResultSuccess from "src/components/CreditRatingResultSuccess";
import {
    areScoresValid,
    getRequestPayloadForPatchPerson,
    getRequestPayloadForSoftCreditPull,
} from "src/components/CreditRatingStepWizard/utils";
import { postSoftCreditPull } from "src/services/softCreditPull";
import { switcher } from "src/utils";
import { logError } from "src/utils/logger";
import { Deal } from "Types/api/Deal";
import { VehicleState } from "Types/state";
import "./CreditRatingStepWizard.scss";

export interface StepControls {
    cancel?: any;
    next?: any;
}

interface CreditRatingStepWizardState {
    step: number;
    lowScore: number | undefined;
    highScore: number | undefined;
    isLoading: boolean;
}

export interface CreditRatingStepWizardProps {
    applyCreditRating: (score: number) => void;
    initialFormData: Partial<CreditRatingFormState>;
    isDrawer: boolean;
    patchDealPerson: (uuid: string, person: Deal["person"]) => void;
    setIsViewingSoftCreditRatingForm: (isViewing: boolean) => void;
    setShowSlider: (showSlider: boolean) => void;
    updateAnnualEstimatedIncome: (income: number) => void;
    uuid: string;
    vehicles: VehicleState[];
    isViewingSoftCreditRatingForm: boolean;
}

class CreditRatingStepWizard extends Component<CreditRatingStepWizardProps, CreditRatingStepWizardState> {
    constructor(props: CreditRatingStepWizardProps) {
        super(props);

        this.state = {
            highScore: undefined,
            isLoading: false,
            lowScore: undefined,
            step: this.props.isViewingSoftCreditRatingForm ? 2 : 1,
        } as CreditRatingStepWizardState;
    }

    public render() {
        return (
            <div className={"CreditRatingStepWizard__container"}>
                {this.state.isLoading ? <LoadingBar /> : this.showStep()}
            </div>
        );
    }

    private showStep = () => {
        return switcher({
            1: <CreditRatingInitial cancel={this.cancel} next={this.handleInitialClick} />,
            2: <CreditRatingDisclaimer cancel={this.cancel} next={this.next} />,
            3: (
                <CreditRatingForm
                    initialFormData={this.props.initialFormData}
                    isDrawer={this.props.isDrawer}
                    cancelForm={this.cancel}
                    submitForm={this.submitRequest}
                />
            ),
            4: (
                <CreditRatingResultSuccess
                    lowScore={this.state.lowScore}
                    highScore={this.state.highScore}
                    cancel={this.cancel}
                    next={this.applyCreditRating}
                />
            ),
            5: <CreditRatingResultNoMatch cancel={this.cancel} />,
        })(1)(this.state.step);
    };

    private next = (event?: React.MouseEvent<HTMLButtonElement>) => {
        if (event) {
            event.preventDefault();
        }
        this.setState({
            step: this.state.step + 1,
        });
    };

    private cancel = (event?: React.MouseEvent<HTMLButtonElement>) => {
        if (event) {
            event.preventDefault();
        }
        this.setState({
            step: 1,
        });
        this.props.setShowSlider(true);
        this.props.setIsViewingSoftCreditRatingForm(false);
    };

    private handleInitialClick = (event?: React.MouseEvent<HTMLButtonElement>) => {
        this.props.setShowSlider(false);
        this.props.setIsViewingSoftCreditRatingForm(true);
        analyticsFEA.engine.softPullCreditAppStart(this.props.uuid, this.props.vehicles);
        this.next(event);
    };

    private submitRequest = async (data: CreditRatingFormState) => {
        this.setState({ isLoading: true });

        this.props.updateAnnualEstimatedIncome(data.income);
        this.props.patchDealPerson(this.props.uuid, getRequestPayloadForPatchPerson(data));

        try {
            const response = await postSoftCreditPull(this.props.uuid, getRequestPayloadForSoftCreditPull(data));
            const score = response.score;

            if (areScoresValid(score && score.low, score && score.high)) {
                this.setState({ isLoading: false, step: 4, lowScore: score.low, highScore: score.high });
            } else {
                this.setState({ isLoading: false, step: 5, lowScore: undefined, highScore: undefined });
            }
        } catch (e) {
            this.setState({ isLoading: false, step: 5, lowScore: undefined, highScore: undefined });
            logError(e, {
                error: e,
                message: "Error while attempting to send soft credit pull",
                state: {
                    form: {
                        ...getRequestPayloadForSoftCreditPull(data),
                        year: "removed for privacy",
                    },
                    step: this.state,
                },
            });
        }
    };

    private applyCreditRating = () => {
        const { uuid, vehicles } = this.props;
        const { lowScore, highScore } = this.state;

        if (areScoresValid(lowScore, highScore)) {
            this.props.applyCreditRating((lowScore! + highScore!) / 2);
            this.props.setIsViewingSoftCreditRatingForm(false);

            analyticsFEA.engine.softPullCreditAppComplete(uuid, vehicles, undefined);
        }
    };
}

export default CreditRatingStepWizard;
